import { useEffect, useState } from "react";
import client from "@/lib/api";
import { RefreshCw, MessageCircle, AlertCircle, Trophy, Crown, Medal, Sparkles } from "lucide-react";

const BOT_LABEL = "Discord Territoria Bot";

/**
 * Discord community leaderboard, sourced from the Discord Territoria Bot's
 * public leaderboard for the Territoria guild via our /api/discord-leaderboard
 * proxy (10-min cached).
 *
 * States:
 *  - loading  → skeleton rows
 *  - locked   → MEE6 leaderboard is not public yet (401) → clear CTA
 *  - error    → generic error + retry
 *  - loaded   → top-25 with rank, avatar, username, level, xp
 */
export default function DiscordLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await client.get("/discord-leaderboard", { params: refresh ? { refresh: true } : {} });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load Discord leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, []);

  return (
    <div className="space-y-5" data-testid="discord-leaderboard">
      {/* Section header */}
      <div className="panel p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">// Community · {BOT_LABEL}</span>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase mt-1">Discord Community Leaderboard</h2>
          {data?.guild?.name && (
            <p className="text-xs text-[var(--t-text-dim)] mt-1">
              {data.guild.name}
              {typeof data.guild.member_count === "number" ? ` · ${data.guild.member_count.toLocaleString()} members` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="btn-ghost !text-xs"
            data-testid="discord-leaderboard-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="panel border-[var(--t-red)] p-4 flex items-start gap-3" data-testid="discord-leaderboard-error">
          <AlertCircle className="w-5 h-5 text-[var(--t-red)] flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-display uppercase text-sm text-[var(--t-red)] tracking-[0.08em]">Unable to load leaderboard</div>
            <p className="text-xs text-[var(--t-text-dim)] mt-1 font-mono break-words">{error}</p>
          </div>
        </div>
      )}

      {/* Not-public state */}
      {!loading && !error && data && data.public === false && (
        <div className="panel corner-frame p-6 md:p-8 text-center relative overflow-hidden" data-testid="discord-leaderboard-locked">
          <div className="absolute -inset-2 pointer-events-none bg-[radial-gradient(circle_at_center,_rgba(88,101,242,0.15)_0%,_transparent_70%)]" />
          <div className="relative">
            <MessageCircle className="w-10 h-10 text-[#9ca6ff] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase mb-2">
              Leaderboard preparing to unlock
            </h3>
            <p className="text-sm text-[var(--t-text-dim)] max-w-xl mx-auto leading-relaxed">
              The {BOT_LABEL} public leaderboard is currently disabled. Once an admin re-enables it, this section will populate automatically.
            </p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="panel overflow-hidden" data-testid="discord-leaderboard-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-3 md:px-5 py-3 border-b border-[var(--t-border)] last:border-0 animate-pulse">
              <div className="w-9 h-4 bg-[var(--t-panel-2)]" />
              <div className="w-8 h-8 rounded-full bg-[var(--t-panel-2)]" />
              <div className="flex-1 h-4 bg-[var(--t-panel-2)] max-w-[200px]" />
              <div className="w-16 h-4 bg-[var(--t-panel-2)]" />
              <div className="w-20 h-4 bg-[var(--t-panel-2)]" />
            </div>
          ))}
        </div>
      )}

      {/* Loaded leaderboard */}
      {!loading && !error && data && data.public !== false && data.players && data.players.length > 0 && (
        <>
          {/* Podium (top 3) */}
          {data.players.length >= 3 && (
            <div className="panel p-5 md:p-6" data-testid="discord-leaderboard-podium">
              <div className="flex items-end justify-between gap-3 md:gap-5">
                <DiscordPodiumSlot entry={data.players[1]} tier="silver" />
                <DiscordPodiumSlot entry={data.players[0]} tier="gold" />
                <DiscordPodiumSlot entry={data.players[2]} tier="bronze" />
              </div>
            </div>
          )}

          {/* Rows */}
          <div className="panel overflow-hidden" data-testid="discord-leaderboard-list">
            <div className="hidden md:grid grid-cols-[80px_1fr_120px_140px] gap-4 px-5 py-3 border-b border-[var(--t-border)] bg-gradient-to-r from-[rgba(88,101,242,0.06)] to-transparent font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase">
              <span>Rank</span>
              <span>Citizen</span>
              <span className="text-right">Level</span>
              <span className="text-right">XP</span>
            </div>
            <ul>
              {data.players.map((p) => (
                <DiscordRow key={p.user_id || p.rank} entry={p} />
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase px-1">
            <span data-testid="discord-leaderboard-count">// {data.players.length} shown</span>
            <span>
              {data.cached ? `cached · ${Math.floor((data.cache_age_seconds || 0) / 60)}m ${(data.cache_age_seconds || 0) % 60}s ago` : "live"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const TIER_META = {
  gold:   { color: "#f3c552", glow: "rgba(243,197,82,0.55)", Icon: Crown, size: "lg", label: "Gold" },
  silver: { color: "#d6deec", glow: "rgba(214,222,236,0.45)", Icon: Medal, size: "md", label: "Silver" },
  bronze: { color: "#cd7f32", glow: "rgba(205,127,50,0.55)", Icon: Medal, size: "md", label: "Bronze" },
};

function DiscordPodiumSlot({ entry, tier }) {
  const [imgOk, setImgOk] = useState(true);
  if (!entry) return <div className="flex-1" />;
  const meta = TIER_META[tier];
  const Icon = meta.Icon;
  const isLarge = meta.size === "lg";
  const showImg = entry.avatar_url && imgOk;
  return (
    <div className="flex-1 flex flex-col items-center text-center" data-testid={`discord-podium-${tier}`}>
      <Icon
        className={`${isLarge ? "w-7 h-7 md:w-8 md:h-8" : "w-5 h-5 md:w-6 md:h-6"} mb-2`}
        style={{ color: meta.color, filter: `drop-shadow(0 0 10px ${meta.glow})` }}
        strokeWidth={1.4}
      />
      <div
        className={`relative ${isLarge ? "w-20 h-20 md:w-24 md:h-24" : "w-14 h-14 md:w-16 md:h-16"} flex items-center justify-center border bg-[#050912] mb-3 rounded-full overflow-hidden`}
        style={{ borderColor: meta.color, boxShadow: `inset 0 0 20px ${meta.glow}, 0 0 24px ${meta.glow}` }}
      >
        {showImg ? (
          <img
            src={entry.avatar_url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <span className={`font-display ${isLarge ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`} style={{ color: meta.color }}>
            {(entry.username || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
        <span
          className={`absolute -top-2 -right-2 inline-flex items-center justify-center ${isLarge ? "w-7 h-7 text-xs" : "w-6 h-6 text-[10px]"} font-display border bg-[#050912] rounded-full`}
          style={{ borderColor: meta.color, color: meta.color, boxShadow: `0 0 10px ${meta.glow}` }}
        >
          {entry.rank}
        </span>
      </div>
      <div
        className={`font-display ${isLarge ? "text-lg md:text-xl" : "text-sm md:text-base"} uppercase leading-tight truncate max-w-full px-1`}
        style={{ color: meta.color, textShadow: `0 0 14px ${meta.glow}` }}
      >
        {entry.username}
      </div>
      <div className={`mt-2 font-mono ${isLarge ? "text-sm md:text-base" : "text-xs md:text-sm"} text-[var(--t-text)]`}>
        Lv. {entry.level} · {formatXp(entry.xp)} XP
      </div>
      <div className="mt-1 font-mono text-[9px] tracking-[0.25em] uppercase" style={{ color: meta.color }}>
        {meta.label}
      </div>
    </div>
  );
}

function DiscordRow({ entry }) {
  const [imgOk, setImgOk] = useState(true);
  const topThree = entry.rank <= 3;
  const tierMeta = topThree ? TIER_META[["gold", "silver", "bronze"][entry.rank - 1]] : null;
  const tierColor = tierMeta ? tierMeta.color : "var(--t-text)";
  const showImg = entry.avatar_url && imgOk;
  return (
    <li
      data-testid={`discord-row-${entry.rank}`}
      className={`flex items-center gap-3 md:grid md:grid-cols-[80px_1fr_120px_140px] md:gap-4 px-3 md:px-5 py-3 border-b border-[var(--t-border)] last:border-0 hover:bg-[rgba(88,101,242,0.06)] transition ${
        topThree ? "bg-[rgba(88,101,242,0.03)]" : ""
      }`}
    >
      <div className="font-display text-base md:text-lg leading-none flex-shrink-0 w-9 md:w-auto" style={{ color: tierColor }}>
        #{entry.rank}
      </div>
      <div className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-1">
        {showImg ? (
          <img
            src={entry.avatar_url}
            alt=""
            className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover border border-[var(--t-border)] flex-shrink-0"
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[rgba(88,101,242,0.15)] border border-[var(--t-border-glow)] flex items-center justify-center text-[#9ca6ff] font-mono text-xs flex-shrink-0">
            {(entry.username || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div
            className="font-display text-sm md:text-base uppercase truncate leading-tight"
            style={tierMeta ? { color: tierColor, textShadow: `0 0 10px ${tierMeta.glow}` } : undefined}
          >
            {entry.username}
          </div>
          {typeof entry.message_count === "number" && (
            <div className="font-mono text-[9px] md:text-[10px] tracking-[0.15em] uppercase text-[var(--t-text-muted)] truncate">
              {entry.message_count.toLocaleString()} messages
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5 md:hidden flex-shrink-0">
        <span className="font-mono text-xs text-[var(--t-text)]">Lv. {entry.level}</span>
        <span className="font-mono text-[10px] text-[var(--t-text-muted)]">{formatXp(entry.xp)} XP</span>
      </div>
      <div className="hidden md:flex md:items-center md:justify-end gap-1.5 font-mono text-sm text-[var(--t-text)]">
        <Sparkles className="w-3.5 h-3.5 text-[var(--t-blue-glow)]" />
        {entry.level}
      </div>
      <div className="hidden md:block font-mono text-sm text-right text-[var(--t-text)]">
        {formatXp(entry.xp)}
      </div>
    </li>
  );
}

function formatXp(v) {
  if (typeof v !== "number") return "0";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}
